import type { RecordingId } from "@repo/app-types/entities/recording";
import type {
  CallOutlineSegment,
  TeammateAccountCallOutline,
} from "@repo/app-types/entities/teammateAccountCallOutline";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { DateTime, Duration } from "luxon";
import { useEffect, useId, useRef, useState, type FC } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  buildCallNavigationItems,
  checkCallSegmentContainsPosition,
  type CallPosition,
} from "./callNavigationHelpers.js";

export interface CallNavigatorProps {
  recordings: readonly AccountGradingRecording[];
  callOutline: TeammateAccountCallOutline | undefined;
  isOutlineLoading?: boolean;
  isOutlineError?: boolean;
  /** The recording selected in the transcript audio player. */
  expandedRecordingId: RecordingId | undefined;
  /** Shows every call's key moments without disclosure controls. */
  expandAll?: boolean;
  /** The key moment under the audio playhead. */
  audioPosition?: CallPosition;
  /** The key moment nearest the transcript's reading position. */
  scrollPosition?: CallPosition;
  onCallRequest: (recordingId: RecordingId) => void;
  onSegmentRequest: (position: CallPosition) => void;
}

export interface CallNavigationRowProps {
  callNumber: number;
  recordedAt: string;
  title: string;
  keyMoment?: CallOutlineSegment;
  /** Keeps the key-moment line's space when no moment matches the current position. */
  reserveKeyMomentSpace?: boolean;
  isExpanded?: boolean;
  isStuck?: boolean;
  chaptersId?: string;
  onPress?: () => void;
  className?: string;
}

export const CallNavigationRow: FC<CallNavigationRowProps> = (props) => {
  const {
    callNumber,
    recordedAt,
    title,
    keyMoment,
    reserveKeyMomentSpace,
    isExpanded,
    isStuck,
    chaptersId,
    onPress,
    className,
  } = props;

  return (
    <AriaButton
      data-call-header
      className={twMerge(
        "sticky top-0 z-10 flex w-full items-start justify-between gap-2 rounded-lg bg-card px-2.5 py-1.5 text-left hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:outline-none",
        isStuck &&
          "rounded-t-none after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-3 after:h-3 after:bg-linear-to-b after:from-card after:to-transparent after:content-['']",
        className,
      )}
      aria-expanded={isExpanded}
      aria-controls={isExpanded ? chaptersId : undefined}
      onPress={onPress}
    >
      <span className="min-w-0">
        <span className="block text-2xs font-normal text-muted-foreground">
          Call {callNumber} &middot;{" "}
          {DateTime.fromISO(recordedAt).toLocaleString(DateTime.DATETIME_MED)}
        </span>
        <span className="mt-0.5 block text-xs font-semibold text-foreground">
          {title}
        </span>
        {keyMoment || reserveKeyMomentSpace ? (
          <span className="mt-1 flex min-h-4 items-start gap-2 text-xs font-normal text-muted-foreground">
            {keyMoment ? (
              <>
                <span className="shrink-0 tabular-nums">
                  {Duration.fromMillis(keyMoment.start_timestamp).toFormat(
                    "h:mm:ss",
                  )}
                </span>
                <span>{keyMoment.title}</span>
              </>
            ) : null}
          </span>
        ) : null}
      </span>
      {isExpanded === undefined ? null : isExpanded ? (
        <IconChevronUp className="mt-3 size-4 shrink-0 text-muted-foreground" />
      ) : (
        <IconChevronDown className="mt-3 size-4 shrink-0 text-muted-foreground" />
      )}
    </AriaButton>
  );
};

export interface CallKeyMomentRowProps {
  segment: CallOutlineSegment;
  isAudioActive: boolean;
  isScrollCurrent: boolean;
  onPress: () => void;
}

export const CallKeyMomentRow: FC<CallKeyMomentRowProps> = (props) => {
  const { segment, isAudioActive, isScrollCurrent, onPress } = props;

  return (
    <AriaButton
      className={twMerge(
        "flex w-full items-start gap-3 rounded-lg px-2.5 py-2 text-left text-xs hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:outline-none",
        isScrollCurrent && "bg-gray-50",
        isAudioActive && "bg-gray-100 hover:bg-gray-100",
      )}
      aria-current={isAudioActive ? "location" : undefined}
      onPress={onPress}
    >
      <span className="w-16 shrink-0 font-normal text-muted-foreground tabular-nums">
        {Duration.fromMillis(segment.start_timestamp).toFormat("h:mm:ss")}
      </span>
      <span className="min-w-0 font-medium">{segment.title}</span>
    </AriaButton>
  );
};

/** Navigates the calls and chapters available in one grading. */
export const CallNavigator: FC<CallNavigatorProps> = (props) => {
  const {
    recordings,
    callOutline,
    isOutlineLoading,
    isOutlineError,
    expandedRecordingId,
    expandAll = false,
    audioPosition,
    scrollPosition,
    onCallRequest,
    onSegmentRequest,
  } = props;
  const calls = buildCallNavigationItems(recordings, callOutline);
  const navigationId = useId();
  const navRef = useRef<HTMLElement>(null);
  const [stuckCallId, setStuckCallId] = useState<RecordingId>();

  useEffect(() => {
    const activeCall = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>(
        "[data-call-recording-id]",
      ) ?? [],
    ).find(
      (element) => element.dataset.callRecordingId === expandedRecordingId,
    );
    activeCall?.scrollIntoView({ block: "nearest" });
  }, [expandedRecordingId]);

  useEffect(() => {
    const nav = navRef.current;
    const scrollPane = nav?.closest<HTMLElement>(
      "[data-call-scroll-container]",
    );
    if (!nav || !scrollPane) {
      return;
    }

    const updateStuckCall = () => {
      const stickyTop =
        scrollPane.getBoundingClientRect().top +
        Number.parseFloat(getComputedStyle(scrollPane).paddingTop);
      const stuckSection = Array.from(
        nav.querySelectorAll<HTMLElement>("[data-call-recording-id]"),
      ).find((section) => {
        const bounds = section.getBoundingClientRect();
        const headerHeight =
          section.querySelector<HTMLElement>("[data-call-header]")
            ?.offsetHeight ?? 0;
        return (
          bounds.top < stickyTop - 1 && bounds.bottom > stickyTop + headerHeight
        );
      });
      const nextId = recordings.find(
        (recording) => recording.id === stuckSection?.dataset.callRecordingId,
      )?.id;
      setStuckCallId((currentId) =>
        currentId === nextId ? currentId : nextId,
      );
    };

    scrollPane.addEventListener("scroll", updateStuckCall, { passive: true });
    window.addEventListener("resize", updateStuckCall);
    updateStuckCall();
    return () => {
      scrollPane.removeEventListener("scroll", updateStuckCall);
      window.removeEventListener("resize", updateStuckCall);
    };
  }, [callOutline, expandedRecordingId, expandAll, recordings]);

  return (
    <nav ref={navRef} aria-label="Calls">
      <ol className="space-y-2">
        {calls.map(({ recording, callNumber, title, segments }) => {
          const isExpanded = expandAll || recording.id === expandedRecordingId;
          const chaptersId = `${navigationId}-call-${recording.id}-chapters`;

          return (
            <li key={recording.id} data-call-recording-id={recording.id}>
              <CallNavigationRow
                callNumber={callNumber}
                recordedAt={recording.recorded_at}
                title={title}
                isExpanded={expandAll ? undefined : isExpanded}
                isStuck={stuckCallId === recording.id}
                chaptersId={chaptersId}
                onPress={() => {
                  if (expandAll || !isExpanded) onCallRequest(recording.id);
                }}
              />

              {isExpanded ? (
                <div id={chaptersId}>
                  {segments.length > 0 ? (
                    <ol>
                      {segments.map((segment) => {
                        const isAudioActive = checkCallSegmentContainsPosition(
                          recording.id,
                          segment,
                          audioPosition,
                        );
                        const isScrollCurrent =
                          checkCallSegmentContainsPosition(
                            recording.id,
                            segment,
                            scrollPosition,
                          );

                        return (
                          <li
                            key={`${segment.title}-${segment.start_timestamp}-${segment.end_timestamp}`}
                          >
                            <CallKeyMomentRow
                              segment={segment}
                              isAudioActive={isAudioActive}
                              isScrollCurrent={isScrollCurrent}
                              onPress={() =>
                                onSegmentRequest({
                                  recordingId: recording.id,
                                  timestamp: segment.start_timestamp,
                                })
                              }
                            />
                          </li>
                        );
                      })}
                    </ol>
                  ) : isOutlineLoading ? (
                    <div className="px-3 py-2">
                      <DefaultSkeleton />
                    </div>
                  ) : isOutlineError ? (
                    <p className="px-3 py-2 text-sm text-destructive">
                      Failed to load call chapters.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
